/**
 * Configuration validation utilities
 * Validates OcChatConfig before widget initialization
 */

import { EMPTY_GUID } from '../../../constants';
import { OcChatConfig } from '../../../types/oc';

/**
 * Validation error with field information
 */
export class ConfigValidationError extends Error {
  constructor(
    message: string,
    public field?: string,
  ) {
    super(message);
    this.name = 'ConfigValidationError';
  }
}

/**
 * Validates GUID format (with or without hyphens)
 */
const isValidGuid = (value: string): boolean => {
  const guidRegex =
    /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i;
  return guidRegex.test(value);
};

/**
 * Validates required string field
 */
const validateRequiredString = (
  value: unknown,
  fieldName: string,
): void => {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new ConfigValidationError(
      `${fieldName} is required and must be a non-empty string`,
      fieldName,
    );
  }
};

/**
 * Validates OcChatConfig object
 * Throws ConfigValidationError if validation fails
 */
export const validateOcChatConfig = (config: unknown): void => {
  // Check config object exists
  if (!config || typeof config !== 'object') {
    throw new ConfigValidationError('Configuration object is required');
  }

  const cfg = config as Record<string, unknown>;

  // Validate required fields
  validateRequiredString(cfg.aiAssistantKey, 'aiAssistantKey');
  validateRequiredString(cfg.tenantId, 'tenantId');

  const requireLogin = cfg.requireLogin !== false;
  if (requireLogin) {
    validateRequiredString(cfg.loginUrl, 'loginUrl');
  }

  if (cfg.aiAssistantKey === EMPTY_GUID) {
    if (cfg.apiTransport === 'ocSite') {
      // ocSite resolves the agent from aiAssistantKey, so a recipe code is only needed when the host
      // has no real key to send (it renders the empty GUID when the tenant has no system agent row).
      validateRequiredString(cfg.recipeCode, 'recipeCode');
    } else {
      // Crest rejects the empty key outright and takes no recipeCode to fall back on, so an embed
      // configured this way can only fail as a runtime 400 on the first call. Say so at init.
      throw new ConfigValidationError(
        'aiAssistantKey must be a real AI Assistant key when apiTransport is crest',
        'aiAssistantKey',
      );
    }
  }

  // Note: Authentication is validated at runtime via API calls, not at config validation time.
  // crest: iamKey or cookie-based auth. ocSite: antiforgery + OC host tenant context.

  // Validate aiAssistantKey is a valid GUID
  if (!isValidGuid(cfg.aiAssistantKey as string)) {
    throw new ConfigValidationError(
      'aiAssistantKey must be a valid GUID format',
      'aiAssistantKey',
    );
  }
};

/**
 * Type guard to check if config is valid
 * Returns true if valid, false otherwise
 */
export const isValidOcChatConfig = (config: unknown): config is OcChatConfig => {
  try {
    validateOcChatConfig(config);
    return true;
  } catch {
    return false;
  }
};

